// src/components/pat/PatActionLegitimacyPanel.jsx
// ------------------------------------------------------------
// PAT-GPT — Causal Model Evaluation and Action Scope
//
// Didactic version:
// • Explains what the user MAY do with the model
// • Explains what they MUST NOT infer
// • Separates structural permission from belief stability
// ------------------------------------------------------------

import React from "react";

/* ------------------------------------------------------------
   Badge styling (defensive)
------------------------------------------------------------ */
function badgeClasses(labelRaw) {
  if (typeof labelRaw !== "string") {
    return "bg-slate-800 border-slate-600 text-slate-300";
  }

  const v = labelRaw.toLowerCase();

  if (
    v.startsWith("justified") ||
    v.startsWith("identifiable") ||
    v.startsWith("front-door") ||
    v.startsWith("mediated/pathway")
  ) {
    return "bg-emerald-900/40 border-emerald-700/80 text-emerald-200";
  }

  if (
    v.startsWith("exploratory") ||
    v.startsWith("conditional") ||
    v.includes("under assumptions") ||
    v.includes("collider") ||
    v.includes("adjustment caution") ||
    v.includes("role-edge conflict")
  ) {
    return "bg-amber-900/40 border-amber-700/80 text-amber-200";
  }

  if (v.startsWith("unidentifiable") || v.startsWith("not")) {
    return "bg-rose-900/40 border-rose-700/80 text-rose-200";
  }

  return "bg-slate-800 border-slate-600 text-slate-300";
}

/* ------------------------------------------------------------
   Plain-language permission framing
------------------------------------------------------------ */
function actionPermissionText(scopeStatus) {
  if (
    scopeStatus.startsWith("justified") ||
    scopeStatus === "structurally_supported"
  ) {
    return (
      "The confirmed causal structure supports intervention reasoning. " +
      "The model is structurally sufficient to explore how changes to causes " +
      "would affect the outcome."
    );
  }

  if (
    scopeStatus.startsWith("exploratory") ||
    scopeStatus.startsWith("conditionally")
  ) {
    return (
      "The confirmed causal structure supports exploratory reasoning about intervention " +
      "under explicit assumptions. It does not provide a standalone basis for action."
    );
  }

  return (
    "The confirmed structure supports causal explanation; however, does not " +
    "support intervention or counterfactual reasoning."
  );
}

/* ------------------------------------------------------------
   Credence explanation (BCSM → lay meaning)
------------------------------------------------------------ */
function credenceExplanation(credence) {
  if (!credence) return null;

  const band = credence.band;

  if (band === "high_stability") {
    return (
      "This causal interpretation is internally robust. Reasonable changes " +
      "to assumptions or variable definitions are unlikely to overturn the " +
      "model’s conclusions."
    );
  }

  if (band === "moderate_stability") {
    return (
      "This causal interpretation is coherent, yet sensitive to modelling " +
      "choices. Conclusions should be treated as provisional."
    );
  }

  if (band === "fragile_stability") {
    return (
      "This causal interpretation is plausible yet unstable. Small changes " +
      "to assumptions, structure, or variable meaning could lead to different " +
      "causal conclusions."
    );
  }

  return (
    "This causal interpretation is highly unstable. The structure should be " +
    "used only for exploratory or descriptive purposes."
  );
}

/* ------------------------------------------------------------
   Legitimacy × Credence coupling (single didactic sentence)
------------------------------------------------------------ */
function legitimacyCredenceCoupling(scopeStatus, credence) {
  if (!credence) return null;

  if (
    scopeStatus.startsWith("justified") ||
    scopeStatus === "structurally_supported"
  ) {
    return (
      "Although intervention reasoning is structurally permitted, the stability " +
      "of that reasoning depends on the causal credence shown below."
    );
  }

  if (
    scopeStatus.startsWith("exploratory") ||
    scopeStatus.startsWith("conditionally")
  ) {
    return (
      "Intervention reasoning remains exploratory, and the causal credence " +
      "below indicates how fragile those assumptions are."
    );
  }

  return (
    "Because intervention is not structurally licensed, the causal credence " +
    "below should be read as describing interpretive coherence only."
  );
}

/* ------------------------------------------------------------
   Main component
------------------------------------------------------------ */

function normaliseScope(actionLegitimacy) {
  const explicit = actionLegitimacy?.intervention_scope;
  if (explicit && typeof explicit === "object") {
    return {
      status: explicit.status || "unknown",
      label: explicit.label || "Not assessed",
      rationale: explicit.rationale || "",
    };
  }

  const legacyVerdict = actionLegitimacy?.verdict || "Not assessed";
  const verdictLower =
    typeof legacyVerdict === "string" ? legacyVerdict.toLowerCase() : "";

  let label = legacyVerdict;
  let status = "unknown";

  if (verdictLower.startsWith("conditionally")) {
    label = "Exploratory only";
    status = "exploratory_only";
  } else if (verdictLower.startsWith("justified")) {
    status = "structurally_supported";
  } else if (verdictLower.startsWith("exploratory")) {
    status = "exploratory_only";
  } else if (verdictLower.startsWith("not")) {
    status = "not_available";
  }

  return {
    status,
    label,
    rationale: "",
  };
}

function inferDisplayIdentifiabilityMethod(actionLegitimacy) {
  const explicit = actionLegitimacy?.causal_identifiability;

  const method =
    actionLegitimacy?.criteria?.identifiability_method ||
    explicit?.status ||
    "unspecified";

  const methodLower =
    typeof method === "string" ? method.toLowerCase() : "unspecified";

  const adjustmentSet = Array.isArray(
    actionLegitimacy?.criteria?.adjustment_set,
  )
    ? actionLegitimacy.criteria.adjustment_set
    : [];

  const adjustmentSetLooksMediatorOnly =
    adjustmentSet.length > 0 &&
    adjustmentSet.every((item) => /^M\d+$/i.test(String(item).trim()));

  if (
    methodLower === "frontdoor" ||
    (methodLower === "identifiable_under_adjustment" &&
      adjustmentSetLooksMediatorOnly)
  ) {
    return "frontdoor";
  }

  return methodLower;
}

function normaliseIdentifiability(actionLegitimacy) {
  const explicit = actionLegitimacy?.causal_identifiability;
  const methodLower = inferDisplayIdentifiabilityMethod(actionLegitimacy);

  if (methodLower === "frontdoor") {
    return {
      status: "frontdoor",
      label: "Mediated/pathway identification",
      rationale:
        "The confirmed graph authorises a mediated/pathway structure involving X, M, and Y. If a direct X → Y edge is also present, the result is a combined direct-plus-mediated structure rather than a pure pathway-only route. The mediator role is licensed by the confirmed edges, not by the M label alone.",
    };
  }

  if (explicit && typeof explicit === "object") {
    const explicitStatus = explicit.status || methodLower || "unspecified";

    if (explicitStatus === "identifiable_under_adjustment") {
      return {
        status: explicitStatus,
        label: explicit.label || "Identifiable under adjustment",
        rationale:
          explicit.rationale ||
          "The confirmed graph supports identification of an intervention effect, conditional on a valid candidate confounder-adjustment strategy.",
      };
    }

    return {
      status: explicitStatus,
      label: explicit.label || "Unspecified",
      rationale: explicit.rationale || "",
    };
  }

  let label = "Unspecified";
  if (methodLower === "unidentifiable") label = "Unidentifiable";
  else if (methodLower === "identifiable_under_adjustment")
    label = "Identifiable under adjustment";
  else if (methodLower === "identifiable_under_assumptions")
    label = "Identifiable under assumptions";
  else if (methodLower === "identifiable" || methodLower === "direct")
    label = "Identifiable";

  return {
    status: methodLower,
    label,
    rationale: "",
  };
}

export default function PatActionLegitimacyPanel({
  actionLegitimacy,
  bcsm,
  doPanel,
}) {
  const status = actionLegitimacy?.status || "unknown";

  const scope = normaliseScope(actionLegitimacy);
  const actionIdentifiability = normaliseIdentifiability(actionLegitimacy);

  const identifiability =
    doPanel?.method && doPanel?.badge
      ? {
          status: doPanel.method,
          label: doPanel.badge,
          rationale: doPanel.body || actionIdentifiability.rationale || "",
        }
      : actionIdentifiability;

  const scopeStatus = scope.status || "unknown";
  const scopeLabel = scope.label || "Not assessed";
  const scopeRationale = scope.rationale || "";

  const identStatus = identifiability.status || "unspecified";
  const identLabel = identifiability.label || "Unspecified";
  const identRationale = identifiability.rationale || "";

  const credence = bcsm?.credence_scale;

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-5 space-y-5">
      {/* Header */}
      <div className="text-xl font-semibold text-emerald-300">
        Intervention Scope and Model Limits
      </div>

      {/* Intervention scope */}
      {status === "pending_dag_confirmation" ? (
        <p className="text-sm text-slate-400 leading-snug">
          {actionLegitimacy?.rationale ||
            "Intervention scope will be assessed after a causal structure is confirmed."}
        </p>
      ) : (
        <>
          <div className="space-y-3">
            <p className="text-sm text-slate-300 font-medium">
              Intervention scope
            </p>

            <div
              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs ${
                scopeLabel.toLowerCase().startsWith("exploratory")
                  ? "bg-amber-900/40 border-amber-700/80 text-amber-200"
                  : badgeClasses(scopeLabel)
              }`}
            >
              {scopeLabel}
            </div>
            <p className="text-sm text-slate-400 leading-snug">
              {scopeRationale || actionPermissionText(scopeStatus)}
            </p>
          </div>

          {credence && (
            <p className="text-xs text-slate-500 leading-snug italic">
              {legitimacyCredenceCoupling(scopeStatus, credence)}
            </p>
          )}
        </>
      )}

      {/* Identifiability */}
      <div className="space-y-3">
        <p className="text-sm text-slate-300 font-medium">
          Causal identifiability
        </p>

        <div
          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs ${badgeClasses(
            identLabel,
          )}`}
        >
          {identLabel}
        </div>

        <p className="text-sm text-slate-400 leading-snug">
          {identRationale ||
            (identStatus === "unidentifiable"
              ? "No unique intervention effect can be derived from the current structure."
              : identStatus === "collider_caution"
                ? "The confirmed DAG contains a collider or collider-risk structure. Adjustment should not proceed without checking whether conditioning would open rather than block a path."
                : identStatus === "role_edge_conflict"
                  ? "The confirmed DAG contains a mismatch between declared variable roles and the authorised edge structure. The structural interpretation should be reviewed before using the model for intervention reasoning."
                  : identStatus === "frontdoor"
                    ? "The confirmed graph authorises a mediated/pathway structure involving X, M, and Y. If a direct X → Y edge is also present, the result is a combined direct-plus-mediated structure rather than a pure pathway-only route. The mediator role is licensed by the confirmed edges, not by the M label alone."
                    : identStatus === "identifiable_under_adjustment"
                      ? "The confirmed graph supports identification of an intervention effect, conditional on a valid candidate confounder-adjustment strategy."
                      : identStatus === "identifiable_under_assumptions"
                        ? "The confirmed graph supports identification of an intervention effect under explicit structural assumptions."
                        : "The confirmed graph supports identification of an intervention effect from the declared causal structure.")}
        </p>

        <p className="text-sm text-slate-400">
          This reflects a structural property of the confirmed graph, not a
          judgement about empirical truth.
        </p>
      </div>

      {/* Credence / stability */}
      {credence && (
        <div className="space-y-2">
          <p className="text-sm text-slate-300 font-medium">
            Causal credence (model stability)
          </p>
          <p className="text-sm text-slate-400">
            Stability level:{" "}
            <span className="text-slate-200">{credence.label}</span>
          </p>
          <p className="text-sm text-slate-400 leading-snug">
            {credenceExplanation(credence)}
          </p>
          <p className="text-xs text-slate-500 italic">
            This is a qualitative diagnostic of internal coherence, not an
            empirical probability or effect size.
          </p>
        </div>
      )}

      {/* How to use */}
      <div className="space-y-2">
        <p className="text-sm text-slate-300 font-medium">
          How to interpret this result
        </p>
        <p className="text-sm text-slate-400 leading-snug">
          {identStatus === "frontdoor"
            ? "Read this as support for pathway-based intervention reasoning within the confirmed DAG. The model can be used to examine how X may relate to Y through the authorised mediator pathway and, where present, alongside a direct X → Y edge. It should not be read as evidence that the intervention will work, that the mediator fully explains the effect, or that the pathway has been empirically validated."
            : "Use this model to clarify assumptions, compare scenarios, and explore possible causal pathways. Do not treat it as evidence that an intervention will succeed without independent empirical support."}
        </p>
      </div>

      {/* Hard boundary */}
      <div className="text-xs text-slate-500 italic">
        This assessment does not establish empirical truth, effect magnitude,
        policy correctness, or real-world feasibility. Those judgements remain
        external to the causal structure itself.
      </div>
    </div>
  );
}
